import { createDateTimeColumn } from '@app/components/date/create-date-time-column';
import { LTime } from '@app/components/date/LTime';
import { ApplyColumnFilter } from '@app/components/table/ApplyColumnFilter';
import { createActionsColumn } from '@app/modules/audit/components/create-actions-column';
import { createCustomIdColumn } from '@app/modules/audit/components/create-custom-id-column';
import { createStatusColumn } from '@app/modules/audit/components/create-status-column';
import { AuditMatch } from '@mi-front/core';
import { MRT_ColumnDef, MRT_Row } from 'material-react-table';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { MatchName } from '../components/audit-result/MatchName';
import { MatchStatuses } from '../components/audit-result/MatchStatuses';

interface AuditMatchColumnOptions {
  /**
   * Type of time column to display
   * - 'time': Shows only time using LTime component
   * - 'datetime': Shows date and time using LDateTime component
   */
  timeColumnType?: 'time' | 'datetime';

  /**
   * Custom time/date column configuration
   * If provided, replaces the default time column
   * If null, time column is omitted
   */
  timeColumn?: MRT_ColumnDef<AuditMatch> | null;

  /**
   * Custom audio column configuration
   * If provided, replaces the default audio column
   */
  audioColumn?: Partial<MRT_ColumnDef<AuditMatch>> | null;

  /**
   * Custom status column configuration
   * If provided, replaces the default status column
   */
  statusColumn?: Partial<MRT_ColumnDef<AuditMatch>> | null;

  /**
   * Custom actions column cell renderer
   * Component that receives the row and returns the actions UI
   * If null, actions column is omitted
   */
  actionsColumn?: ((row: MRT_Row<AuditMatch>) => ReactNode) | null;

  /** Override the default Acciones column width (150 for 3 icon buttons). */
  actionsColumnSize?: number;

  /**
   * Additional columns to include
   */
  extraColumns?: MRT_ColumnDef<AuditMatch>[];

  /**
   * Columns inserted immediately after the custom ID / audio column
   */
  afterCustomIdColumns?: MRT_ColumnDef<AuditMatch>[];
}

/**
 * Hook that generates common column definitions for audit match tables.
 * Allows customization of individual columns while maintaining consistency.
 */
export function useAuditMatchColumns(
  options: AuditMatchColumnOptions = {},
): MRT_ColumnDef<AuditMatch>[] {
  const { $t } = useIntl();
  const {
    timeColumnType = 'time',
    timeColumn,
    audioColumn,
    statusColumn,
    actionsColumn,
    actionsColumnSize = 150,
    extraColumns = [],
    afterCustomIdColumns = [],
  } = options;

  const columns: MRT_ColumnDef<AuditMatch>[] = [];

  // Time/Date column (included by default, customizable or can be omitted)
  if (timeColumn !== null) {
    // If custom timeColumn is provided, use it
    if (timeColumn) {
      columns.push(timeColumn);
    } else {
      // Otherwise, use default based on timeColumnType
      if (timeColumnType === 'time') {
        columns.push({
          id: 'time',
          header: $t({
            id: 'audit.props.time',
            defaultMessage: 'Hora',
          }),
          size: 110,
          Cell: ({ row: { original: match } }) => (
            <LTime value={match.datetime} second={'numeric'} />
          ),
        });
      } else if (timeColumnType === 'datetime') {
        columns.push(
          createDateTimeColumn<AuditMatch>($t, {
            accessor: (match) => match.datetime,
            size: 130,
          }),
        );
      }
    }
    // If timeColumn is explicitly null, no time column is added
  }

  // Audio column (always included by default, customizable)
  if (audioColumn !== null) {
    columns.push({
      ...createCustomIdColumn<AuditMatch>($t, {
        id: 'audio.customId',
        accessor: (match) => match.audio?.customId,
        size: 140,
        enableColumnFilter: false,
      }),
      Cell: ({ row }) => (
        <ApplyColumnFilter field="customId" value={row.original.audio?.customId}>
          <MatchName match={row.original} />
        </ApplyColumnFilter>
      ),
      ...audioColumn,
    });
    columns.push(...afterCustomIdColumns);
  }

  // Status column (with default implementation)
  if (statusColumn !== null) {
    columns.push(
      createStatusColumn<AuditMatch>($t, {
        accessorKey: 'status',
        size: statusColumn?.size ?? 130,
        Cell: ({ row }) => <MatchStatuses match={row.original} />,
        ...statusColumn,
      }),
    );
  }

  // Actions column (always included by default, customizable)
  if (actionsColumn !== null) {
    columns.push(
      createActionsColumn($t, {
        size: actionsColumnSize,
        Cell: ({ row }) => (actionsColumn ? actionsColumn(row) : null),
      }),
    );
  }

  // Add any extra columns
  columns.push(...extraColumns);

  return columns;
}
